import BigNumber from 'bignumber.js';

import type { ApiLiquidityHubResource, LiquidityHubSource, Token } from 'types';
import { areAddressesEqual } from 'utilities/areAddressesEqual';
import { convertMantissaToTokens } from 'utilities/convertMantissaToTokens';
import convertUsdMantissaToCents from 'utilities/convertUsdMantissaToCents';
import { formatApiRewardDistributors } from 'utilities/formatApiRewardDistributors';

export const formatToLiquidityHubResource = ({
  apiResource,
  tokens,
  underlyingToken,
  tokenPriceCents,
}: {
  apiResource: ApiLiquidityHubResource;
  tokens: Token[];
  underlyingToken: Token;
  tokenPriceCents: BigNumber;
}): LiquidityHubSource => {
  const collateralTokens = tokens.filter(token =>
    apiResource.exposure.some(tokenAddress => areAddressesEqual(token.address, tokenAddress)),
  );

  const resourceLockEndDate = apiResource.lockEndTime
    ? new Date(apiResource.lockEndTime)
    : undefined;

  const allocationTokens = convertMantissaToTokens({
    value: new BigNumber(apiResource.allocationMantissa),
    token: underlyingToken,
  });

  const allocationCentsFromApi = convertUsdMantissaToCents(apiResource.allocationUsdMantissa ?? 0);
  const allocationCents = apiResource.allocationUsdMantissa
    ? allocationCentsFromApi
    : allocationTokens.multipliedBy(tokenPriceCents);

  const { supplyTokenDistributions } = formatApiRewardDistributors({
    apiRewardDistributors: apiResource.rewardsDistributors,
    tokens,
    supplyBalanceDollars: allocationCents.dividedBy(100),
    borrowBalanceDollars: new BigNumber(0),
  });

  const allocationCapTokens = convertMantissaToTokens({
    value: new BigNumber(apiResource.capMantissa ?? 0),
    token: underlyingToken,
  });

  const allocationCapCentsFromApi = convertUsdMantissaToCents(apiResource.capUsdMantissa ?? 0);
  const allocationCapCents = apiResource.capUsdMantissa
    ? allocationCapCentsFromApi
    : allocationCapTokens.multipliedBy(tokenPriceCents);

  const liquidityTokens = convertMantissaToTokens({
    value: new BigNumber(apiResource.liquidityMantissa),
    token: underlyingToken,
  });

  const liquidityCents = liquidityTokens.multipliedBy(tokenPriceCents);

  return {
    name: apiResource.name ?? 'Resource',
    address: apiResource.resourceAddress,
    allocationTokens,
    allocationCents,
    allocationCapCents,
    liquidityTokens,
    liquidityCents,
    supplyApyPercentage: new BigNumber(apiResource.apyRatio).multipliedBy(100),
    supplyTokenDistributions,
    collateralTokens,
    lockEndDate: resourceLockEndDate,
  };
};
